/**
 * NewsMicroActionsRow — Like / Save / Share / Hỏi Asklaw on each card.
 *
 * Anonymous user storage: localStorage `asklaw_news_action_{action}` JSON
 * Set<news_id>. Authed user storage: POST /api/v1/me/news/action (Phase 2,
 * Day-1 still localStorage-only per founder defer of citation linker; auth
 * piggy-back lands later).
 *
 * Share: Web Share API mobile, fallback copy-link to clipboard.
 *
 * Reduced-motion: no transition on icon state changes (global CSS rule
 * neutralises transitions automatically).
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../../use-lang';
import { resolveHotNews } from '../../i18n/hotnews';

const LIKE_KEY = 'asklaw_news_liked';
const SAVE_KEY = 'asklaw_news_saved';

export interface NewsMicroActionsRowProps {
  newsId: string;
  slug: string;
  headline: string;
}

export function NewsMicroActionsRow({ newsId, slug, headline }: NewsMicroActionsRowProps) {
  const { lang } = useLang();
  const copy = resolveHotNews(lang);

  const [liked, setLiked] = useState<boolean>(() => readSet(LIKE_KEY).has(newsId));
  const [saved, setSaved] = useState<boolean>(() => readSet(SAVE_KEY).has(newsId));
  const [shareToast, setShareToast] = useState<boolean>(false);

  // Re-read on mount in case localStorage updated by another tab.
  useEffect(() => {
    setLiked(readSet(LIKE_KEY).has(newsId));
    setSaved(readSet(SAVE_KEY).has(newsId));
  }, [newsId]);

  function toggleSet(key: string, set: (v: boolean) => void, current: boolean) {
    const s = readSet(key);
    if (current) {
      s.delete(newsId);
    } else {
      s.add(newsId);
    }
    writeSet(key, s);
    set(!current);
  }

  async function handleShare() {
    const url = `${window.location.origin}/news/${slug}`;
    try {
      if (typeof navigator !== 'undefined' && 'share' in navigator) {
        await navigator.share({ title: headline, url });
        return;
      }
    } catch {
      // user cancelled or share API failed — fall back
    }
    try {
      await navigator.clipboard.writeText(url);
      setShareToast(true);
      window.setTimeout(() => setShareToast(false), 1800);
    } catch {
      // clipboard blocked — open prompt fallback
      window.prompt(copy.action_share, url);
    }
  }

  const askPrefill = encodeURIComponent(
    copy.ask_prefill_hint.replace('{headline}', headline),
  );

  return (
    <div role="toolbar" aria-label="news actions" style={rowStyle}>
      <ActionButton
        label={liked ? copy.action_like_active : copy.action_like}
        active={liked}
        onClick={() => toggleSet(LIKE_KEY, setLiked, liked)}
        icon={<HeartIcon filled={liked} />}
      />
      <ActionButton
        label={saved ? copy.action_save_active : copy.action_save}
        active={saved}
        onClick={() => toggleSet(SAVE_KEY, setSaved, saved)}
        icon={<BookmarkIcon filled={saved} />}
      />
      <ActionButton
        label={shareToast ? copy.action_share_copied : copy.action_share}
        active={shareToast}
        onClick={handleShare}
        icon={<ShareIcon />}
      />
      <Link
        to={`/chat?q=${askPrefill}&context_news=${newsId}`}
        className="cta-primary"
        style={askCtaStyle}
        aria-label={copy.ask_cta_aria}
      >
        {copy.ask_cta_card}
        <span className="arrow" aria-hidden>{'->'}</span>
      </Link>
    </div>
  );
}

function ActionButton({
  label,
  active,
  onClick,
  icon,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 10px',
        background: 'transparent',
        border: '1px solid var(--asklaw-border)',
        borderRadius: 8,
        fontFamily: 'var(--font-sans)',
        fontSize: 13,
        fontWeight: 500,
        color: active ? 'var(--asklaw-orange-deep)' : 'var(--asklaw-ink-soft)',
        cursor: 'pointer',
      }}
    >
      {icon}
      {label}
    </button>
  );
}

function HeartIcon({ filled }: { filled: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill={filled ? 'currentColor' : 'none'}>
      <path
        d="M7 12s-4.5-2.6-4.5-6A2.5 2.5 0 0 1 7 4.2a2.5 2.5 0 0 1 4.5 1.8c0 3.4-4.5 6-4.5 6Z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BookmarkIcon({ filled }: { filled: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill={filled ? 'currentColor' : 'none'}>
      <path
        d="M3.5 2h7v10l-3.5-2.5L3.5 12V2Z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path
        d="M10 2 7 5M7 5 4 2M7 5v6M3 8v3.5h8V8"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function readSet(key: string): Set<string> {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) return new Set(arr.filter((v) => typeof v === 'string'));
    return new Set();
  } catch {
    return new Set();
  }
}

function writeSet(key: string, s: Set<string>): void {
  try {
    window.localStorage.setItem(key, JSON.stringify([...s]));
  } catch {
    // ignore quota / private mode
  }
}

const rowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 6,
  marginTop: 4,
};

const askCtaStyle: React.CSSProperties = {
  marginLeft: 'auto',
  padding: '6px 14px',
  fontSize: 13,
};
